// Firebase bootstrap — inicializado uma única vez no carregamento do módulo.
//
// As chaves são lidas de variáveis Vite (import.meta.env.VITE_FIREBASE_*) que
// vêm dos arquivos .env.development (dev local → projeto staging) e
// .env.production (build → projeto prod). Nenhuma decisão em runtime; o Vite
// já escolhe o arquivo pelo modo do build.
//
// Estas chaves são IDs públicos do projeto Firebase por design. A segurança
// dos dados vem das Firestore Security Rules + regras de Auth, não da chave.

import { initializeApp } from 'firebase/app';
import { getAuth, indexedDBLocalPersistence, initializeAuth } from 'firebase/auth';
import {
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore';

// App nativo (Capacitor)? A flag é injetada no <head> pelo build do app,
// antes de qualquer módulo — decide os ajustes de WebView abaixo.
const IS_NATIVE = typeof window !== 'undefined' && !!window.__MAPIARE_NATIVE__;

const firebaseConfig = {
  apiKey:            import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId:             import.meta.env.VITE_FIREBASE_APP_ID,
};

// Falha cedo e explicitamente se alguma chave essencial estiver faltando.
for (const [k, v] of Object.entries(firebaseConfig)) {
  if (!v && k !== 'storageBucket' && k !== 'messagingSenderId') {
    throw new Error(`Firebase config ausente: VITE_FIREBASE_${k.replace(/[A-Z]/g, m => '_' + m).toUpperCase()}`);
  }
}

export const app = initializeApp(firebaseConfig);

// No app nativo (Capacitor), o `getAuth()` padrão tenta carregar o iframe do
// gapi (fluxo popup/redirect) — que falha na origem `capacitor://localhost`
// (iOS) e deixa o onAuthStateChanged pendurado pra sempre (loader infinito).
// Fix documentado do @capacitor-firebase/authentication: `initializeAuth` com
// persistência IndexedDB e SEM popupRedirectResolver. O login Google nativo
// não usa popup (signInWithCredential), então nada se perde.
// No site, segue o `getAuth()` normal (popup do Google precisa do resolver).
export const auth = IS_NATIVE
  ? initializeAuth(app, { persistence: indexedDBLocalPersistence })
  : getAuth(app);

// Firestore com PERSISTÊNCIA OFFLINE (cache em IndexedDB): leituras ficam
// disponíveis sem internet e escritas feitas offline entram numa fila que
// sincroniza sozinha ao reconectar. `persistentMultipleTabManager` deixa
// várias abas compartilharem o mesmo cache (site). Se o ambiente não suportar
// IndexedDB (ex: modo privado), cai pro cache em memória — sem quebrar o boot.
//
// NOTA iOS: já testamos forçar long-polling + desligar fetch-streams no app
// (tentativa de contornar o bug do WKWebView do SIMULADOR). NÃO resolveu o
// simulador e deixou a home lenta, então revertemos — o app usa a MESMA config
// do site. O bug do Firestore no simulador é do WKWebView (device real
// funciona, ver docs/APP_STORE_PREP.md); não vale pagar o custo de performance.
let _db;
try {
  _db = initializeFirestore(app, {
    localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
  });
} catch (e) {
  console.warn('[firebase] persistência offline indisponível, seguindo sem cache persistente:', e?.message);
  _db = getFirestore(app);
}
export const db = _db;

// Conveniência para debug no console em dev
if (import.meta.env.DEV) {
  // eslint-disable-next-line no-undef
  globalThis.__firebase = { app, auth, db, projectId: firebaseConfig.projectId };
  // eslint-disable-next-line no-console
  console.info('[firebase] conectado em', firebaseConfig.projectId);
}
